import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Actions,
  BackIcon,
  BrandHeaderRow,
  BrandLogo,
  Button,
  Card,
  Field,
  Header,
  HeaderIconButton,
  HeaderText,
  Input,
  Message,
  Muted,
  Page,
  Shell,
  Subtitle,
  Title
} from '../components/PageTheme';
import useBackNavigation from '../utils/useBackNavigation';

const Form = styled.form`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) max-content;
  gap: 0.85rem;
  align-items: end;

  button {
    white-space: nowrap;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContactList = styled.div`
  display: grid;
  gap: 0.85rem;
  margin-top: 1rem;
`;

const HeaderActions = styled(Actions)`
  align-self: flex-start;
  margin-left: auto;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.1);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.045);

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const ContactActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: flex-end;

  @media (max-width: 640px) {
    width: 100%;

    button {
      flex: 1 1 8rem;
    }
  }
`;

const spin = `
  to {
    transform: rotate(360deg);
  }
`;

const Spinner = styled.span`
  width: 18px;
  height: 18px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: contact-spin 0.75s linear infinite;

  @keyframes contact-spin {
    ${spin}
  }
`;

const ContactName = styled.div`
  color: #f8fafc;
  font-size: 1.15rem;
  font-weight: 950;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  margin-top: 0.45rem;
  padding: 0.18rem 0.5rem;
  border: 1px solid rgba(226, 232, 240, 0.16);
  border-radius: 999px;
  color: #cbd5e1;
  font-size: 0.78rem;
  font-weight: 850;
  text-transform: capitalize;
`;

const ManageContacts = () => {
  const { user } = useAuth();
  const goBack = useBackNavigation('/create-alarm');
  const [contacts, setContacts] = useState([]);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
  const [deletingContactId, setDeletingContactId] = useState(null);
  const [copyingInviteId, setCopyingInviteId] = useState(null);
  const [copiedInviteId, setCopiedInviteId] = useState(null);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${window.RUNTIME_CONFIG.apiUrl}/contacts`, {
        headers: { Authorization: `Bearer ${await user.getIdToken()}` }
      });
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setError('Failed to fetch contacts');
    }
  }, [user]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const email = newEmail.trim().toLowerCase();

    if (!newName.trim() || !email) {
      setError('Please provide both name and email');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setSavingContact(true);
    try {
      const response = await axios.post(
        `${window.RUNTIME_CONFIG.apiUrl}/contacts`,
        {
          name: newName.trim(),
          emailAddress: email,
          notificationChannel: 'email'
        },
        {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        }
      );

      if (response.data) {
        setNewName('');
        setNewEmail('');
        setError('');
        setContacts(prevContacts => [...prevContacts, response.data]);
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      setError(error.response?.data?.message || 'Failed to save contact. Please ensure the email is valid.');
    } finally {
      setSavingContact(false);
    }
  };

  const handleCopyInvite = async (contact) => {
    setCopyingInviteId(contact.id);
    setCopiedInviteId(null);
    setError('');

    try {
      const response = contact.inviteUrl
        ? { data: { inviteUrl: contact.inviteUrl } }
        : await axios.post(
            `${window.RUNTIME_CONFIG.apiUrl}/contacts/${contact.id}/invite`,
            {},
            {
              headers: { Authorization: `Bearer ${await user.getIdToken()}` }
            }
          );

      await navigator.clipboard.writeText(response.data.inviteUrl);
      setContacts(prevContacts => prevContacts.map(prevContact => (
        prevContact.id === contact.id
          ? { ...prevContact, inviteUrl: response.data.inviteUrl, consentStatus: 'pending' }
          : prevContact
      )));
      setCopiedInviteId(contact.id);
    } catch (error) {
      console.error('Error copying invite link:', error);
      setError(error.response?.data?.message || 'Failed to copy invite link');
    } finally {
      setCopyingInviteId(null);
    }
  };

  const handleDelete = async (contactId) => {
    if (confirmingDeleteId !== contactId) {
      setConfirmingDeleteId(contactId);
      return;
    }

    setDeletingContactId(contactId);
    setError('');
    try {
      await axios.delete(
        `${window.RUNTIME_CONFIG.apiUrl}/contacts/${contactId}`,
        {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        }
      );
      setConfirmingDeleteId(null);
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      setError('Failed to delete contact');
    } finally {
      setDeletingContactId(null);
    }
  };

  return (
    <Page>
      <Shell>
        <Header>
          <HeaderText>
            <BrandHeaderRow>
              <BrandLogo />
              <HeaderActions>
                <HeaderIconButton type="button" aria-label="Back" onClick={goBack}>
                  <BackIcon aria-hidden="true" />
                </HeaderIconButton>
              </HeaderActions>
            </BrandHeaderRow>
            <Title>Accountability contacts.</Title>
            <Subtitle>Save the people who may receive an email if future-you loses the wager.</Subtitle>
          </HeaderText>
        </Header>

        <Card>
          <Form onSubmit={handleSubmit}>
            <Field>
              Name
              <Input
                type="text"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder="Contact name"
                required
              />
            </Field>
            <Field>
              Email
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={newEmail}
                onChange={(event) => setNewEmail(event.target.value)}
                placeholder="friend@example.com"
                required
              />
            </Field>
            <Button type="submit" disabled={savingContact}>
              {savingContact && <Spinner />}
              {savingContact ? 'Adding...' : 'Add Contact'}
            </Button>
          </Form>

          {error && <Message $error>{error}</Message>}

          <ContactList>
            {contacts.map((contact) => (
              <ContactItem key={contact.id}>
                <div>
                  <ContactName>{contact.name}</ContactName>
                  <Muted>{contact.emailAddress}</Muted>
                  <StatusPill>{contact.consentStatus || 'pending'}</StatusPill>
                </div>
                <ContactActions>
                  {(contact.consentStatus || 'pending') !== 'accepted' && (
                    <Button
                      $secondary
                      type="button"
                      disabled={copyingInviteId === contact.id}
                      onClick={() => handleCopyInvite(contact)}
                    >
                      {copyingInviteId === contact.id
                        ? 'Copying...'
                        : copiedInviteId === contact.id
                          ? 'Copied'
                          : 'Copy invite'}
                    </Button>
                  )}
                  {confirmingDeleteId === contact.id && (
                    <Button $secondary type="button" onClick={() => setConfirmingDeleteId(null)}>
                      Cancel
                    </Button>
                  )}
                  <Button
                    $danger
                    type="button"
                    disabled={deletingContactId === contact.id}
                    onClick={() => handleDelete(contact.id)}
                  >
                    {deletingContactId === contact.id
                      ? 'Deleting...'
                      : confirmingDeleteId === contact.id
                        ? 'Confirm delete'
                        : 'Delete'}
                  </Button>
                </ContactActions>
              </ContactItem>
            ))}
          </ContactList>
        </Card>
      </Shell>
    </Page>
  );
};

export default ManageContacts;
